import {FC, InputHTMLAttributes, ReactNode, useRef} from 'react';
import {useFormContext} from 'react-hook-form';
import BaseCheckbox from 'components/Form/Base/BaseCheckbox';
import {FormContextBoolean, RequiredRules} from 'components/Form/types';
import {getRegisterRules} from 'components/Form/utils';
import TooltipWrapper from 'components/Tooltip';
import useFormError from 'hooks/useFormErrors';
import useLayout from 'hooks/useLayout';

export type CheckboxProps<T = HTMLInputElement> = RequiredRules<
    InputHTMLAttributes<T>
> & {
    disabledReason?: string;
    helpText?: ReactNode;
    label?: ReactNode;
    name: string;
    type?: never;
};

const Checkbox: FC<CheckboxProps> = ({
    className,
    disabled,
    disabledReason,
    helpText,
    name,
    required,
    rules,
    ...rest
}) => {
    const ref = useRef(null);
    const layout = useLayout(ref);

    const {register} = useFormContext<FormContextBoolean>();

    const fieldError = useFormError(name);

    if (!layout) return <div ref={ref} className="hidden" />;

    const registerRules = getRegisterRules({disabled, required, rules});

    const checkbox = (
        <BaseCheckbox
            className={className}
            error={fieldError}
            helpText={helpText}
            required={!!registerRules?.required}
            {...register(name, registerRules)}
            {...rest}
        />
    );

    const content =
        layout === 'sideBySide' ? (
            <>
                <div ref={ref} aria-label="grid-div" role="gridcell">
                    &nbsp;
                </div>
                <div role="gridcell">{checkbox}</div>
            </>
        ) : (
            checkbox
        );

    return disabled && disabledReason ? (
        <TooltipWrapper message={disabledReason}>{content}</TooltipWrapper>
    ) : (
        content
    );
};

export default Checkbox;
